import {readyAuth} from './readyAuth';
import { auth } from '../firebase';

class MtaDataService {
  constructor() {
    // Firebase Realtime Database 기반 URL (환경변수로 재정의 가능)
    this.firebaseMtaUrl = process.env.REACT_APP_FIREBASE_MTA_URL ||
      'https://visualp-1-default-rtdb.asia-southeast1.firebasedatabase.app/sepa';

    // 로컬/운영 백엔드 API 서버 기준 URL (기존 기능 호환)
    this.baseUrl = process.env.NODE_ENV === 'production' 
      ? '/api' 
      : 'http://localhost:3001/api';
      
    this.CACHE_DURATION = 60 * 60 * 1000; // 1시간
  }

  // Helper: 날짜 키 중 최신 데이터 선택
  _getLatestEntry(dataObj) {
    if (!dataObj || typeof dataObj !== 'object') return null;
    const keys = Object.keys(dataObj).filter(k => /^(\d{8})$/.test(k));
    if (keys.length === 0) return null;
    const latestKey = keys.sort().pop();
    return dataObj[latestKey];
  }

  // 개별 종목 객체 매핑 (results/stocks 공통)
  _mapStock(code, item) {
    const basic = item.basic_info || item || {};

    // 가격 정보 객체 구성 (일부 값이 없으면 기본값)
    const priceInfoRaw = item.price_info || {};
    const priceInfo = {
      current_price: priceInfoRaw.current_price ?? basic.current_price ?? 0,
      change_1d: priceInfoRaw.change_1d ?? basic.price_change_rate ?? 0,
      high_52w: priceInfoRaw.high_52w ?? basic.high_52w ?? 0,
      low_52w: priceInfoRaw.low_52w ?? basic.low_52w ?? 0,
    };

    const analysisDetails = item.analysis_details || item.scores_details || {};

    const patternsObj = item.patterns || analysisDetails.pattern_details || {};

    const mapped = {
      code,
      name: item.name || basic.stock_name || basic.stock_code || code,
      market: basic.market,
      sector: basic.sector || '',
      price_info: priceInfo,
      current_price: priceInfo.current_price,
      price_change: priceInfoRaw.price_change ?? basic.price_change ?? 0,
      price_change_rate: priceInfo.change_1d,
      scores: (() => {
        const sc = item.scores || {};
        // 기본값 보강
        sc.total_score = sc.total_score ?? 0;
        sc.fundamental_grade = sc.fundamental_grade ?? 'N/A';

        if (!sc.details) sc.details = {};
        const trendDet = sc.details.trend = sc.details.trend || (analysisDetails.trend_details || {});
        const patternDet = sc.details.pattern = sc.details.pattern || (analysisDetails.pattern_details || {});
        const rsDet = sc.details.rs = sc.details.rs || (analysisDetails.rs_details || {});
        const investorDet = sc.details.investor = sc.details.investor || (analysisDetails.investor_details || {});

        trendDet.total_trend_score = trendDet.total_trend_score ?? 0;
        patternDet.total_pattern_score = patternDet.total_pattern_score ?? 0;
        rsDet.total_rs_score = rsDet.total_rs_score ?? 0;
        investorDet.total_investor_score = investorDet.total_investor_score ?? 0;

        // 상위 레벨 점수도 채움
        sc.trend_score = sc.trend_score ?? trendDet.total_trend_score;
        sc.pattern_score = sc.pattern_score ?? patternDet.total_pattern_score;
        sc.rs_score = sc.rs_score ?? rsDet.total_rs_score;
        sc.investor_score = sc.investor_score ?? investorDet.total_investor_score;

        return sc;
      })(),
      patterns: patternsObj,
      scores_details: analysisDetails,
      fundamental_summary: item.fundamental_summary || analysisDetails.fundamental_details?.summary || {},
      chart_data: item.chart_data || [],
      ranking: item.ranking || {},
      data_date: item.data_date,
      data_quality: item.data_quality || {},
      timestamps: item.timestamps || {}
    };

    return mapped;
  }

  // 최신 일별 분석 결과 로드 (Firebase)
  async loadDailyAnalysis() {
    try {
      // 캐시 확인
      const cachedData = localStorage.getItem('mta_v2_daily_data');
      const cachedTimestamp = localStorage.getItem('mta_v2_daily_timestamp');

      if (cachedData && cachedTimestamp) {
        const now = new Date().getTime();
        if (now - parseInt(cachedTimestamp) < this.CACHE_DURATION) {
          console.log('캐시된 MTA 일별 데이터를 사용합니다.');
          return JSON.parse(cachedData);
        }
      }

      await readyAuth();
      let token = '';
      if (auth.currentUser) {
        token = await auth.currentUser.getIdToken();
      }

      const response = await fetch(`${this.firebaseMtaUrl}/daily.json?auth=${token}`);
      if (!response.ok) {
        throw new Error('일별 데이터 로드 실패');
      }
      const raw = await response.json();
      const latest = this._getLatestEntry(raw) || raw; // 이미 최종 데이터일 수도 있음
      const transformed = this._transformFirebaseData(latest);

      // 데이터 캐싱
      try {
        localStorage.setItem('mta_v2_daily_data', JSON.stringify(transformed));
        localStorage.setItem('mta_v2_daily_timestamp', new Date().getTime().toString());
      } catch (e) {
        console.warn('MTA 일별 데이터 캐싱 실패:', e);
      }

      return transformed;
    } catch (error) {
      console.error('일별 분석 데이터 로드 실패:', error);
      return null;
    }
  }

  // 최신 주별 분석 결과 로드 (Firebase)
  async loadWeeklyAnalysis() {
    try {
      // 캐시 확인
      const cachedData = localStorage.getItem('mta_v2_weekly_data');
      const cachedTimestamp = localStorage.getItem('mta_v2_weekly_timestamp');

      if (cachedData && cachedTimestamp) {
        const now = new Date().getTime();
        if (now - parseInt(cachedTimestamp) < this.CACHE_DURATION) {
          console.log('캐시된 MTA 주별 데이터를 사용합니다.');
          return JSON.parse(cachedData);
        }
      }

      await readyAuth();
      let token = '';
      if (auth.currentUser) {
        token = await auth.currentUser.getIdToken();
      }

      const response = await fetch(`${this.firebaseMtaUrl}/weekly.json?auth=${token}`);
      if (!response.ok) {
        throw new Error('주별 데이터 로드 실패');
      }
      const raw = await response.json();
      const latest = this._getLatestEntry(raw) || raw;
      const transformed = this._transformFirebaseData(latest);

      // 데이터 캐싱
      try {
        localStorage.setItem('mta_v2_weekly_data', JSON.stringify(transformed));
        localStorage.setItem('mta_v2_weekly_timestamp', new Date().getTime().toString());
      } catch (e) {
        console.warn('MTA 주별 데이터 캐싱 실패:', e);
      }

      return transformed;
    } catch (error) {
      console.error('주별 분석 데이터 로드 실패:', error);
      return null;
    }
  }

  // 특정 날짜의 분석 결과 로드
  async loadAnalysisByDate(date, timeframe) {
    try {
      const response = await fetch(`${this.baseUrl}/${timeframe}/${date}`);
      if (!response.ok) throw new Error(`${timeframe} 데이터 로드 실패`);
      return await response.json();
    } catch (error) {
      console.error(`${timeframe} 분석 데이터 로드 실패:`, error);
      return null;
    }
  }

  // Firebase 데이터 구조를 기존 UI 포맷으로 변환
  _transformFirebaseData(rawData) {
    if (!rawData) return null;

    // 이미 stocks 배열이 존재하면 그대로 사용
    if (Array.isArray(rawData.stocks)) {
      return rawData;
    }

    const transformed = {};

    // 메타데이터
    transformed.metadata = rawData.metadata || rawData.summary || {};

    // stocks 길이 반영
    if (!transformed.metadata.total_stocks && rawData.results) {
      transformed.metadata.total_stocks = Object.keys(rawData.results).length;
    }

    // 섹터 분석 등 기타 데이터 그대로 유지
    if (rawData.sector_analysis) {
      transformed.sector_analysis = rawData.sector_analysis;
    }

    // market_info 매핑
    transformed.market_info = rawData.market_info || {
      investor_trading: rawData.investor_trading || {},
      sector_analysis: rawData.sector_analysis || transformed.sector_analysis || {}
    };

    // 보강: market_info 필드가 없을 경우 기본값 설정
    if (!transformed.market_info) {
      transformed.market_info = {};
    }
    if (!transformed.market_info.investor_trading) {
      transformed.market_info.investor_trading = {};
    }
    if (!transformed.market_info.investor_trading.periods) {
      transformed.market_info.investor_trading.periods = {};
    }
    if (!transformed.market_info.sector_analysis) {
      transformed.market_info.sector_analysis = transformed.sector_analysis || {};
    }

    // results 객체 => stocks 배열로 변환 (이전 버전 호환)
    if (rawData.results && typeof rawData.results === 'object') {
      const stocks = Object.entries(rawData.results).map(([code, item]) => this._mapStock(code, item));
      transformed.stocks = stocks;
    } else if (rawData.stocks && !Array.isArray(rawData.stocks) && typeof rawData.stocks === 'object') {
      // 새 버전: stocks 가 dict 형태인 경우
      const stocks = Object.entries(rawData.stocks).map(([code, item]) => {
        // item 에 code 필드가 없다면 주입
        if (!item.code) item.code = code;
        return this._mapStock(code, item);
      });
      transformed.stocks = stocks;
    }

    if (!transformed.metadata.statistics && Array.isArray(transformed.stocks) && transformed.stocks.length > 0) {
      const totalScores = transformed.stocks.map(s => s.scores?.total_score || 0);
      const avg = totalScores.reduce((sum, v) => sum + v, 0) / totalScores.length;
      const max = Math.max(...totalScores);
      const min = Math.min(...totalScores);
      transformed.metadata.statistics = {
        avg_score: avg,
        max_score: max,
        min_score: min
      };
    }

    return transformed;
  }

  async loadDailyData() {
    const data = await this.loadDailyAnalysis();
    if (!data) {
      throw new Error('일별 데이터를 불러올 수 없습니다.');
    }
    return data;
  }

  async loadWeeklyData() {
    const data = await this.loadWeeklyAnalysis();
    if (!data) {
      throw new Error('주별 데이터를 불러올 수 없습니다.');
    }
    return data;
  }
}

export const mtaDataService = new MtaDataService(); 